<template>
  <article class="address-form">
    <Transition
      name="form-transition"
      mode="out-in"
    >
      <!--details-->
      <div
        v-if="isDetailsComplete && !showEditor && cartStore.cart?.deliveryDetails"
        class="bg-white py-5 shadow-md border border-gray-light h-48"
      >
        <AppUserAddress
          class="mb-2 font-medium text-black"
          :data="cartStore.cart?.deliveryDetails"
          :show-change="true"
          @change-clicked="showEditor = true"
        />

        <p class="px-6">
          {{ cartStore.cart?.deliveryDetails.firstName }} {{ cartStore.cart?.deliveryDetails.lastName }}

          <span class="ml-3">{{ cartStore.cart?.deliveryDetails.phoneNumber }}</span>
        </p>
      </div>

      <!--details form-->
      <div
        v-else
        class="bg-white px-4 md:px-6 lg:px-8 py-5 shadow-md gap-0 gap-y-4 border border-gray-light cart-confirmation-form"
      >
        <div class="col-span-12 mx-auto text-center w-full">
          <span class="text-black font-semibold text-xl w-fit block pt-2 pb-5">
            Edit Address
          </span>

          <div class="flex items-center gap-4 w-full mb-7">
            <TextInput
              v-model="formData.firstName"
              label="Receipt First Name"
              class="w-full max-w-[50%]"
              :validation-error="cartStore.errors && 'firstName' in cartStore.errors && cartStore.errors.firstName[0]"
            />

            <TextInput
              v-model="formData.lastName"
              label="Receipt Last Name"
              class="w-full max-w-[50%]"
              :validation-error="cartStore.errors && 'lastName' in cartStore.errors && cartStore.errors.lastName[0]"
            />
          </div>

          <TextInput
            v-model="formData.phoneNumber"
            label="Recipient mobile number"
            class="mb-7"
            :validation-error="cartStore.errors && 'phoneNumber' in cartStore.errors && cartStore.errors.phoneNumber[0]"
          />

          <TextInput
            v-model="formData.streetAddress"
            label="Street Address"
            class="mb-7"
            :validation-error="cartStore.errors && 'streetAddress' in cartStore.errors && cartStore.errors.streetAddress[0]"
          />

          <TextInput
            v-model="formData.building"
            label="Complex / Building (optional)"

            class="mb-4"
          />
          <span class="w-fit block text-sm -mt-2 mb-7 text-gray-dark font-medium">Complex or Building Name, unit number or floor</span>

          <TextInput
            v-model="formData.suburb"
            label="Suburb"
            class="mb-7"
            :validation-error="cartStore.errors && 'suburb' in cartStore.errors && cartStore.errors.suburb[0]"
          />

          <TextInput
            v-model="formData.city"
            label="City / Town"
            class="mb-7"
            :validation-error="cartStore.errors && 'city' in cartStore.errors && cartStore.errors.city[0]"
          />

          <div
            class="input-field mb-7"
            :class="{'error': cartStore.errors && 'province' in cartStore.errors && cartStore.errors.province[0]}"
          >
            <label>
              Province
            </label>

            <AppDropdown
              class="profile-form-dropdown max-w-110"
              :trigger-text="formData.province"
            >
              <template
                v-for="(province, index) in provinces"
                :key="index"
              >
                <AppDropdownItem @click="formData.province = province">
                  {{ province }}
                </AppDropdownItem>
              </template>
            </AppDropdown>

            <span
              v-if="cartStore.errors && 'province' in cartStore.errors && cartStore.errors.province[0]"
              class="validation-error-message"
            >
              {{ cartStore.errors?.province[0] }}
            </span>
          </div>

          <TextInput
            v-model="formData.postalCode"
            label="Postal Code"
            class="mb-7 max-w-70"
            :validation-error="cartStore.errors && 'postalCode' in cartStore.errors && cartStore.errors.postalCode[0]"
          />

          <div class="flex items-center justify-end w-full gap-2 pb-4 pt-8">
            <AppButton
              v-if="isDetailsComplete"
              :disabled="isSubmitting"
              class="btn btn-outline btn-gray border border-gray hover:bg-gray w-full whitespace-nowrap max-w-45 bg-white !text-gray-dark hover:text-white"
              type="button"
              @click="hideEditor"
            >
              Cancel
            </AppButton>

            <AppButton
              :processing="isSubmitting"
              class="btn btn-solid btn-secondary w-full whitespace-nowrap max-w-45"
              @click="submitForm"
            >
              {{ isSubmitting ? 'Saving Address' : 'Save Address' }}
            </AppButton>
          </div>
        </div>
      </div>
    </transition>
  </article>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';

import { AppButton, AppDropdown, AppDropdownItem, AppUserAddress } from '@/components/app';
import { TextInput } from '@/components/forms/inputs';

import { useFormSubmit } from '@/composables';
import { provinces } from '@/const';
import { useAuthStore, useCartStore } from '@/store';

const router = useRouter();

const { isSubmitting } = useFormSubmit();

const authStore = useAuthStore();
const cartStore = useCartStore();

const isDetailsComplete = defineModel('isDetailsComplete');
const showEditor = defineModel('showEditor');

const formData = ref({
  firstName: cartStore.cart?.deliveryDetails?.firstName || authStore?.user?.firstName,
  lastName: cartStore.cart?.deliveryDetails?.lastName || authStore?.user?.lastName,
  phoneNumber: import.meta.env.DEV ? '069 420 1234' : (cartStore.cart?.deliveryDetails?.phoneNumber || authStore?.user?.phoneNumber),
  streetAddress: cartStore.cart?.deliveryDetails?.streetAddress || authStore?.user?.streetAddress,
  building: cartStore.cart?.deliveryDetails?.building || authStore?.user?.building,
  suburb: cartStore.cart?.deliveryDetails?.suburb || authStore?.user?.suburb,
  province: cartStore.cart?.deliveryDetails?.province || authStore?.user?.province,
  postalCode: cartStore.cart?.deliveryDetails?.postalCode || authStore?.user?.postalCode,
  city: cartStore.cart?.deliveryDetails?.city || authStore?.user?.city,
});

const submitForm = async () => {
  isSubmitting.value = true;

  if (formData.value.phoneNumber !== null) {
    formData.value.phoneNumber = formData.value.phoneNumber.replace(/ /g, '');
  }

  await cartStore.updateCart(formData.value);

  if (cartStore.errors === null) {
    await router.push({ name: 'app_cart_confirmation', hash: '#topOfPage' });

    showEditor.value = false;
  }

  isSubmitting.value = false;
};

const hideEditor = async () => {
  if (isDetailsComplete.value) {
    await router.push({ name: 'app_cart_confirmation', hash: '#topOfPage' });

    setTimeout(() => {
      showEditor.value = false;
      formData.value = cartStore.cart.deliveryDetails;
    }, 300);
  }
};

</script>
